import { useState, useMemo } from 'react';

/**
 * Hook to manage favorite metrics with localStorage
 * @param {string} statementType - 'income', 'balance', or 'cash'
 * @param {Array} metrics - Array of metric objects
 * @returns {Object} - { favorites, toggleFavorite, sortedMetrics, isFavorite }
 */
export const useFavoriteMetrics = (statementType, metrics) => {
    const storageKey = `favoriteMetrics_${statementType}`;

    // Load favorites from localStorage
    const [favorites, setFavorites] = useState(() => {
        const saved = localStorage.getItem(storageKey);
        return saved ? JSON.parse(saved) : [];
    });

    // Toggle favorite
    const toggleFavorite = (metricKey) => {
        const newFavorites = favorites.includes(metricKey)
            ? favorites.filter(k => k !== metricKey)
            : [...favorites, metricKey];

        setFavorites(newFavorites);
        localStorage.setItem(storageKey, JSON.stringify(newFavorites));
    };

    // Check if metric is favorite
    const isFavorite = (metricKey) => favorites.includes(metricKey);

    // Sort metrics: favorites first, then rest
    const sortedMetrics = useMemo(() => {
        const favorited = metrics.filter(m => favorites.includes(m.key));
        const notFavorited = metrics.filter(m => !favorites.includes(m.key));
        return [...favorited, ...notFavorited];
    }, [favorites, metrics]);

    return {
        favorites,
        toggleFavorite,
        sortedMetrics,
        isFavorite
    };
};